import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Input,
  Label,
} from "reactstrap";

/**
 * Props:
 * - isOpen: bool
 * - toggle: fonction pour fermer le modal
 * - roles: liste des rôles [{id, name, code}]
 * - features: liste des features [{id, name, code}]
 * - actions: liste des actions [{id, name, code, feature_id}]
 * - initialRoleId: id du rôle pour pré-remplissage
 * - initialPermissions: array [{feature_id, action_id}] pour pré-remplissage
 * - onSave: fonction(payload) pour envoyer la création / mise à jour
 */
const PermissionModal = ({
  isOpen,
  toggle,
  roles,
  features,
  actions,
  initialRoleId = "",
  initialPermissions = [],
  onSave,
}) => {
  const [selectedRole, setSelectedRole] = useState("");
  const [permissionsMap, setPermissionsMap] = useState({}); 
  // { feature_id: [action_id, ...] }

  // Pré-remplissage lors de l'ouverture du modal
  
  useEffect(() => {
    if (isOpen) {
      setSelectedRole(initialRoleId || "");

      const map = {};

      console.log("INITIAL PERMISSIONS: ",initialRoleId );
      initialPermissions.forEach((perm) => {
        if (!map[perm.feature_id]) map[perm.feature_id] = [];
        map[perm.feature_id].push(perm.action_id);
      });
      setPermissionsMap(map);
    }
  }, [isOpen, initialRoleId, initialPermissions]);

  // Détection du mode édition
  const isEditMode = initialPermissions.length > 0;

  // Toggle d'une action
  const handleActionToggle = (featureId, actionId) => {
    setPermissionsMap((prev) => {
      const current = prev[featureId] ?? [];
      const updated = current.includes(actionId)
        ? current.filter((id) => id !== actionId)
        : [...current, actionId];
      return { ...prev, [featureId]: updated };
    });
  };

  // Préparer le payload pour le backend
  const handleSubmit = () => {
    const payload = {
      role_id: selectedRole,
      permissions: Object.entries(permissionsMap).flatMap(([feature_id, actionIds]) =>
        actionIds.map((action_id) => ({
          feature_id: parseInt(feature_id, 10),
          action_id,
        }))
      ),
    };
    onSave(payload);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        {isEditMode ? "Modifier les permissions" : "Créer des permissions"}
      </ModalHeader>
      <ModalBody>
        {/* Sélection du rôle */}
        <Row className="mb-3">
          <Col md="12">
            <Label for="roleSelect">Rôle</Label>
            <Input
              type="select"
              id="roleSelect"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              disabled={isEditMode} // Bloqué en édition
            >
              <option value="">-- Sélectionner un rôle --</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Input>
          </Col>
        </Row>

        {/* Liste des fonctionnalités et actions */}
        <h6 className="mb-3">Fonctionnalités et actions</h6>
        {features.map((feature, idx) => (
          <div key={feature.id} className="mb-2">
            <Row className="align-items-center py-2">
              <Col md="4"><strong>{feature.name}</strong></Col>
              <Col md="8" className="d-flex flex-wrap">
                {actions
                  .filter(a => !a.feature_id || a.feature_id === feature.id)
                  .map(action => {
                    const isChecked = (permissionsMap[feature.id] ?? []).includes(action.id);
                    return (
                      <div key={action.id} className="form-check me-3 mb-1">
                        <Input
                          type="checkbox"
                          id={`f${feature.id}_a${action.id}`}
                          checked={isChecked}
                          onChange={() => handleActionToggle(feature.id, action.id)}
                        />
                        <Label for={`f${feature.id}_a${action.id}`} className="form-check-label">
                          {action.name}
                        </Label>
                      </div>
                    );
                  })}
              </Col>
            </Row>
            {idx < features.length - 1 && <hr className="my-1" style={{ opacity: 0.2 }} />}
          </div>
        ))}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>Annuler</Button>
        <Button color="primary" onClick={handleSubmit} disabled={!selectedRole}>
          {isEditMode ? "Mettre à jour" : "Créer"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PermissionModal;
